import Vapi from "vuex-rest-api";
import Chance from "chance";
import EventWsClient from "../lib/event-ws-client";
import { API_URL, WSS_URL } from "../config";
import moment from "moment";

const chance = new Chance();

const ws = new EventWsClient({ url: WSS_URL });

function getIDsQueryParams(ids) {
  return ids.map(id => `ids=${id}`).join("&");
}

const pathfinders = new Vapi({
  baseURL: API_URL,
  state: {
    // PATHFINDER
    pathfinder: null,

    // LOOT
    LOOT_PER_DAY: 2,
    HEARTS_TO_LOOT: 3,
    PACKAGES_TO_HEART: 5,
    lastUnlockedLoot: null,
    lootUnlocks: {}
  }
})
  .get({
    action: "getPathfinderById",
    property: "pathfinder",
    path: ({ id }) => `pathfinders/${id}`
  })
  .get({
    action: "getPathfindersByIds",
    path: ({ ids }) => `pathfinders/?${getIDsQueryParams(ids)}`
  })
  .get({
    action: "getPathfindersByLocation",
    path: ({ locationId }) => `pathfinders/?location=${locationId}`
  })
  .get({
    action: "getRandomPathfinders",
    path: ({ count }) => `pathfinders/?random=true&count=${count}`
  })
  .get({
    action: "getPathfinderByName",
    path: ({ name }) => `pathfinders/?name=${name}`
  })
  .post({
    action: "postPathfinder",
    property: "pathfinder",
    path: "pathfinders/"
  })
  .patch({
    action: "patchPathfinder",
    property: "pathfinder",
    path: ({ id }) => `pathfinders/${id}/`
  })
  .put({
    action: "putPathfinder",
    property: "pathfinder",
    path: ({ id }) => `pathfinders/${id}/`
  })
  .getStore();

const getters = {
  maxLoot(state, getters) {
    return (
      getters.lockedItems.length === 0 && getters.lockedElements.length === 0
    );
  },

  dayLootCount(state) {
    if (state.pathfinder && state.lootUnlocks[state.pathfinder.id]) {
      return state.lootUnlocks[state.pathfinder.id].filter(({ time }) => {
        return moment().isSame(moment(time), "day");
      }).length;
    } else {
      return 0;
    }
  },

  dayMaxLoot(state, getters) {
    return getters.dayLootCount >= state.LOOT_PER_DAY;
  },

  newLoot(state) {
    if (!state.pathfinder) return false;
    return (
      state.pathfinder.bond > 0 &&
      state.pathfinder.bond %
        (state.PACKAGES_TO_HEART * state.HEARTS_TO_LOOT) ===
        0
    );
  },

  lockedItems(state, getters, rootState) {
    return state.pathfinder
      ? rootState.items.items.filter(
          item =>
            state.pathfinder.unlocked_items.find(key => key === item.key) ===
            undefined
        )
      : [];
  },

  lockedElements(state, getters, rootState) {
    return state.pathfinder
      ? rootState.elements.elements.filter(
          element =>
            state.pathfinder.unlocked_elements.find(
              key => key === element.key
            ) === undefined
        )
      : [];
  }
};

pathfinders.getters = getters;

function filterWsKeys(pathfinder) {
  return {
    id: pathfinder.id,
    name: pathfinder.name,
    type: pathfinder.type,
    location: pathfinder.location,
    items: pathfinder.items,
    element: pathfinder.element,
    primaryColor: pathfinder.primary_color,
    secondaryColor: pathfinder.secondary_color,
    accentColor: pathfinder.accent_color,
    pattern: pathfinder.pattern
  };
}

/**
 * Shuffles array in place. ES6 version
 * @param {Array} a items An array containing the items.
 */
function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const mutations = {
  SET_PATHFINDER(state, pathfinder) {
    state.pathfinder = pathfinder;
  },

  SOFT_UPDATE(state, properties) {
    state.pathfinder = Object.assign({}, state.pathfinder, properties);
  },

  TOGGLE_ITEM(state, item_key) {
    const i = state.pathfinder.items.indexOf(item_key);
    if (i !== -1) {
      state.pathfinder.items.splice(i, 1);
    } else {
      state.pathfinder.items.push(item_key);
    }
  },

  TOGGLE_ELEMENT(state, element_key) {
    if (state.pathfinder.element === element_key) {
      state.pathfinder.element = null;
    } else {
      state.pathfinder.element = element_key;
    }
  },

  GROW_BOND(state, amount) {
    state.pathfinder.bond += amount;
  },

  UNLOCK_ITEM(state, item) {
    state.pathfinder.unlocked_items.push(item.key);
  },

  UNLOCK_ELEMENT(state, item) {
    state.pathfinder.unlocked_elements.push(item.key);
  }
};

Object.assign(pathfinders.mutations, mutations);

const actions = {
  growBond({ state, commit, dispatch }, amount) {
    commit("GROW_BOND", amount);

    return dispatch("patchPathfinder", {
      params: { id: state.pathfinder.id },
      data: { bond: state.pathfinder.bond }
    });
  },

  toggleElement({ commit }, element_key) {
    commit("TOGGLE_ELEMENT", element_key);
  },

  toggleItem({ commit }, item_key) {
    commit("TOGGLE_ITEM", item_key);
  },

  softUpdatePathfinder({ commit }, properties) {
    commit("SOFT_UPDATE", properties);
  },

  setCurrentPathfinder({ commit }, pathfinder) {
    commit("SET_PATHFINDER", pathfinder);
  },

  spawnPathfinder({ state, dispatch }, location) {
    const params = { id: state.pathfinder.id };
    const data = { location: location.key, spawned: true };

    return new Promise(resolve => {
      dispatch("patchPathfinder", { params, data }).then(_ => {
        ws.send("SPAWN", {
          pathfinder: filterWsKeys(state.pathfinder)
        });
        resolve();
      });
    });
  },

  despawnPathfinder({ state, dispatch }) {
    const params = { id: state.pathfinder.id };
    const data = { location: null, spawned: false };

    return new Promise(resolve => {
      // Send despawn while location is not null
      ws.send("DESPAWN", {
        pathfinder: filterWsKeys(state.pathfinder)
      });
      dispatch("patchPathfinder", { params, data }).then(_ => {
        resolve();
      });
    });
  },

  deployCarePackage({ state, getters, dispatch }, _pathfinder) {
    ws.send("CARE_PACKAGE", { pathfinder: filterWsKeys(state.pathfinder) });

    return new Promise(resolve => {
      if (!(getters.maxLoot || getters.dayMaxLoot)) {
        dispatch("growBond", 1).then(_ => {
          if (getters.newLoot) {
            dispatch("unlockNewLoot").then(loot => {
              state.lastUnlockedLoot = loot;
              state.lootUnlocks[state.pathfinder.id] =
                state.lootUnlocks[state.pathfinder.id] || [];
              state.lootUnlocks[state.pathfinder.id].push({
                time: moment(),
                loot: loot.key
              });
              resolve();
            });
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  },

  unlockNewLoot({ state, rootState, commit, dispatch, getters }) {
    return new Promise(resolve => {
      const loot = chance.pickone(
        getters.lockedItems.concat(getters.lockedElements)
      );

      let data;
      if (rootState.items.items.find(item => item.key === loot.key)) {
        commit("UNLOCK_ITEM", loot);
        data = { unlocked_items: state.pathfinder.unlocked_items };
      }
      if (rootState.elements.elements.find(el => el.key === loot.key)) {
        commit("UNLOCK_ELEMENT", loot);
        data = {
          unlocked_elements: state.pathfinder.unlocked_elements
        };
      }

      if (data) {
        dispatch("patchPathfinder", {
          params: { id: state.pathfinder.id },
          data: data
        }).then(_ => {
          resolve(loot);
        });
      }
    });
  },

  releaseJar({ state }, jar) {
    ws.send("FIREFLIES", {
      color: jar.led
    });
  },

  searchPathfindersByName({ dispatch }, options) {
    return new Promise(resolve => {
      dispatch("getPathfinderByName", { params: { name: options.name } }).then(
        response => {
          let pathfinders = response.data;
          if (options.addRandom) {
            dispatch("getRandomPathfinders", { params: { count: 3 } }).then(
              randomResponse => {
                let randomPathfinders = randomResponse.data;
                pathfinders = pathfinders.concat(randomPathfinders);
                shuffle(pathfinders);
                resolve(pathfinders);
              }
            );
          } else {
            resolve(pathfinders);
          }
        }
      );
    });
  },

  newPathfinder({ commit }) {
    commit("SET_PATHFINDER", {
      id: null,
      name: "",
      avatar: null,
      bond: 0,
      type: "gerbit",
      location: null,
      pattern: "blank",
      primary_color: "#ffffff",
      secondary_color: "#ffffff",
      accent_color: "#ffffff",
      items: [],
      unlocked_items: [],
      element: null,
      unlocked_elements: [],
      firefly_jars: []
    });
  }
};

Object.assign(pathfinders.actions, actions);

export default pathfinders;
